import LoadingSpinner from "components/atoms/LoadingSpinner/LoadingSpinner";
import { useActiveCustomerAccount } from "hooks/useActiveCustomerAccount";
import { useSelectedBuildingModelCarbonReductionPlan } from "hooks/Building/useBuildingModelCarbonReductionPlan";
import styles from "./BuildingPageSettings.module.scss";

interface BuildingPageSettingsProps {}

function safeNumber(value: number | undefined) {
    if (!value) {
        return "not set";
    }
    return value.toFixed(3);
}

function safePct(value: number | undefined) {
    if (!value) {
        return "not set";
    }
    return `${(value * 100.0).toFixed(1)}%`;
}

function BuildingPageSettings({}: BuildingPageSettingsProps) {
    const {
        bmcrp: selectedBMCRP,
        buildingModel,
        isLoading: isLoadingBMCRP,
    } = useSelectedBuildingModelCarbonReductionPlan();
    const { activeCustomerAccount, user, isFetching: isLoadingCustomerAccount } = useActiveCustomerAccount();
    const referencePlan = selectedBMCRP.plan;
    const referencePlanName = selectedBMCRP.name;

    if (isLoadingCustomerAccount || isLoadingBMCRP || !buildingModel || !user || !activeCustomerAccount) {
        return <LoadingSpinner />;
    }

    const appliedPolicies =
        referencePlan.carbonPolicyCosts && referencePlan.carbonPolicyCosts.length > 0
            ? new Set(referencePlan.carbonPolicyCosts.map((c) => c.policyName))
            : "none";

    const buildingPolicies =
        buildingModel.selectedCarbonPolicies && buildingModel.selectedCarbonPolicies.length > 0
            ? new Set(buildingModel.selectedCarbonPolicies.map((p) => p.name))
            : ["none"];

    return (
        <div className={styles.buildingPageSettings}>
            <div style={{ maxWidth: "1024px" }}>
                <div style={{ marginBottom: "15px" }}>
                    Audette applies settings in a cascade, from account to building to plan. Plan takes precendence over
                    building, which takes precendence over account. Below is the map of settings for this account (
                    {activeCustomerAccount.name}), building ({buildingModel.buildingName}) and plan ({referencePlanName}
                    ).
                </div>
                <table>
                    <thead>
                        <tr>
                            <th className={styles.headerWithName}></th>
                            <th className={styles.headerWithLevel}>Account</th>
                            <th className={styles.headerWithLevel}>Building</th>
                            <th className={styles.headerWithLevel}>Plan</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td className={styles.rowWithName}>Carbon Cost Policies Applied</td>
                            <td className={styles.rowWithLevel}>N/A</td>
                            <td className={styles.rowWithLevel}>{buildingPolicies}</td>
                            <td className={styles.rowWithLevel}>{appliedPolicies}</td>
                        </tr>
                        <tr>
                            <td className={styles.rowWithName}>Utility Inflation Rate</td>
                            <td className={styles.rowWithLevel}>
                                {safePct(activeCustomerAccount.defaultUtilityInflationRate)}
                            </td>
                            <td className={styles.rowWithLevel}>
                                {safePct(buildingModel.defaultUtilityInflationRate)}
                            </td>
                            <td className={styles.rowWithLevel}>
                                {safePct(referencePlan.carbonReductionPlanOptions.utilityInflationRate)}
                            </td>
                        </tr>
                        <tr>
                            <td className={styles.rowWithName}>Discount Rate</td>
                            <td className={styles.rowWithLevel}>
                                {safePct(activeCustomerAccount.defaultDiscountRate)}
                            </td>
                            <td className={styles.rowWithLevel}>{safePct(buildingModel.defaultDiscountRate)}</td>
                            <td className={styles.rowWithLevel}>
                                {safePct(referencePlan.carbonReductionPlanOptions.netPresentValueDiscountRate)}
                            </td>
                        </tr>
                        <tr>
                            <td className={styles.rowWithName}>Landlord Share: Cost</td>
                            <td className={styles.rowWithLevel}>
                                {safePct(activeCustomerAccount.defaultLandlordShareCost)}
                            </td>
                            <td className={styles.rowWithLevel}>{safePct(buildingModel.defaultLandlordShareCost)}</td>
                            <td className={styles.rowWithLevel}>
                                {safePct(referencePlan.carbonReductionPlanOptions.defaultLandlordShareCost)}
                            </td>
                        </tr>
                        <tr>
                            <td className={styles.rowWithName}>Landlord Share: Electricity</td>
                            <td className={styles.rowWithLevel}>
                                {safePct(activeCustomerAccount.defaultLandlordShareElectricity)}
                            </td>
                            <td className={styles.rowWithLevel}>
                                {safePct(buildingModel.defaultLandlordShareElectricity)}
                            </td>
                            <td className={styles.rowWithLevel}>
                                {safePct(referencePlan.carbonReductionPlanOptions.defaultLandlordShareElectricity)}
                            </td>
                        </tr>
                        <tr>
                            <td className={styles.rowWithName}>Landlord Share: Natural Gas</td>
                            <td className={styles.rowWithLevel}>
                                {safePct(activeCustomerAccount.defaultLandlordShareNaturalGas)}
                            </td>
                            <td className={styles.rowWithLevel}>
                                {safePct(buildingModel.defaultLandlordShareNaturalGas)}
                            </td>
                            <td className={styles.rowWithLevel}>
                                {safePct(referencePlan.carbonReductionPlanOptions.defaultLandlordShareNaturalGas)}
                            </td>
                        </tr>
                        <tr style={{ display: "none" }}>
                            <td className={styles.rowWithName}>Landlord Share: Steam</td>
                            <td className={styles.rowWithLevel}>
                                {safePct(activeCustomerAccount.defaultLandlordShareSteam)}
                            </td>
                            <td className={styles.rowWithLevel}>{safePct(buildingModel.defaultLandlordShareSteam)}</td>
                            <td className={styles.rowWithLevel}>
                                {safePct(referencePlan.carbonReductionPlanOptions.defaultLandlordShareSteam)}
                            </td>
                        </tr>
                        <tr>
                            <td className={styles.rowWithName}>Electricity Starting Rate ($)</td>
                            <td className={styles.rowWithLevel}>N/A</td>
                            <td className={styles.rowWithLevel}>
                                {safeNumber(buildingModel.defaultElectricityUtilityRate)}
                            </td>
                            <td className={styles.rowWithLevel}>
                                {safeNumber(referencePlan.carbonReductionPlanOptions.userProvidedElectricityRate)}
                            </td>
                        </tr>
                        <tr>
                            <td className={styles.rowWithName}>Natural Gas Starting Rate ($)</td>
                            <td className={styles.rowWithLevel}>N/A</td>
                            <td className={styles.rowWithLevel}>
                                {safeNumber(buildingModel.defaultNaturalGasUtilityRate)}
                            </td>
                            <td className={styles.rowWithLevel}>
                                {safeNumber(referencePlan.carbonReductionPlanOptions.userProvidedNaturalGasRate)}
                            </td>
                        </tr>
                        <tr style={{ display: "none" }}>
                            <td className={styles.rowWithName}>Steam Starting Rate ($)</td>
                            <td className={styles.rowWithLevel}>N/A</td>
                            <td className={styles.rowWithLevel}>{safeNumber(buildingModel.defaultSteamUtilityRate)}</td>
                            <td className={styles.rowWithLevel}>"not set"</td>
                        </tr>
                        <tr style={{ display: "none" }}>
                            <td className={styles.rowWithName}>CAD vs Local Exchange Rate</td>
                            <td className={styles.rowWithLevel}>
                                {safeNumber(activeCustomerAccount.exchangeRateOverride)}
                            </td>
                            <td className={styles.rowWithLevel}></td>
                            <td className={styles.rowWithLevel}></td>
                        </tr>
                        <tr style={{ display: "none" }}>
                            <td className={styles.rowWithName}>Grid Carbon Intensity Schedule</td>
                            <td className={styles.rowWithLevel}>
                                {activeCustomerAccount.defaultGridIntensityScheduleScenario || "not set"}
                            </td>
                            <td className={styles.rowWithLevel}>
                                {buildingModel.defaultGridIntensityScheduleScenario || "not set"}
                            </td>
                            <td className={styles.rowWithLevel}></td>
                        </tr>
                        {referencePlanName === "Audette Plan" && (
                            <tr>
                                <td className={styles.rowWithName}>Recommendation Weight: Emissions</td>
                                <td className={styles.rowWithLevel}>
                                    {safePct(activeCustomerAccount.defaultWeightEmissionSavings)}
                                </td>
                                <td className={styles.rowWithLevel}>
                                    {safePct(buildingModel.defaultWeightEmissionSavings)}
                                </td>
                                <td className={styles.rowWithLevel}>
                                    {safePct(referencePlan.carbonReductionPlanOptions.weightEmissionSavings)}
                                </td>
                            </tr>
                        )}
                        {referencePlanName === "Audette Plan" && (
                            <tr>
                                <td className={styles.rowWithName}>Recommendation Weight: NPV</td>
                                <td className={styles.rowWithLevel}>
                                    {safePct(activeCustomerAccount.defaultWeightNetPresentValue)}
                                </td>
                                <td className={styles.rowWithLevel}>
                                    {safePct(buildingModel.defaultWeightNetPresentValue)}
                                </td>
                                <td className={styles.rowWithLevel}>
                                    {safePct(referencePlan.carbonReductionPlanOptions.weightNetPresentValue)}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
export default BuildingPageSettings;
